<script>
import { DateTime } from 'luxon';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import deLocale from '@fullcalendar/core/locales/de';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import Layout from '@layouts/main';
import BlockingContainer from '@components/BlockingContainer';

export default {
  components: {
    Layout,
    BlockingContainer,
    FullCalendar,
  },
  data() {
    return {
      loading: false,
      selectedEvent: null,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin],
        initialView: 'dayGridMonth',
        locales: [deLocale],
        locale: this.$i18n.locale,
        firstDay: 1,
        height: 'auto',
        nowIndicator: true,
        dayMaxEvents: true,
        nextDayThreshold: '00:00:00',
        displayEventTime: false,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek',
        },
        events: this.fetchEvents,
        eventClick: this.openEvent,
        eventDidMount: this.setEventTitle,
      },
    };
  },
  page() {
    return {
      title: this.$t('titles.calendar'),
    };
  },
  watch: {
    '$i18n.locale'(locale) {
      this.$refs.calendar.getApi().setOption('locale', locale);
    },
  },
  methods: {
    fetchEvents(fetchInfo, successCallback, failureCallback) {
      this.loading = true;

      this.$http
        .get('calendar', {
          params: {
            start: DateTime.fromJSDate(fetchInfo.start).toFormat('yyyy-MM-dd'),
            end: DateTime.fromJSDate(fetchInfo.end)
              .minus({ days: 1 })
              .toFormat('yyyy-MM-dd'),
          },
        })
        .then((response) => {
          const events = Array.isArray(response.data) ? response.data : response.data.data || [];
          successCallback(events.map(this.toCalendarEvent));
        })
        .catch((error) => {
          failureCallback(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toCalendarEvent(event) {
      const isReported = event.source === 'reported';

      if (event.source === 'leave') {
        return {
          id: event.id,
          title: this.$t('messages.leave_time'),
          start: event.start,
          end: event.end,
          display: 'block',
          backgroundColor: '#ffc107',
          borderColor: '#ffc107',
          textColor: '#fff',
          extendedProps: {
            source: event.source,
            amountOfDays: event.amount_of_days,
          },
        };
      }

      return {
        id: event.id,
        title: [event.vip, event.location].filter(Boolean).join(' - '),
        start: event.start,
        end: event.end,
        display: 'block',
        backgroundColor: isReported ? '#28a745' : '#17a2b8',
        borderColor: isReported ? '#28a745' : '#17a2b8',
        textColor: '#fff',
        extendedProps: {
          source: event.source,
          reportId: event.report_id,
          vip: event.vip,
          location: event.location,
          car_type: event.car_type,
          license_plate: event.license_plate,
        },
      };
    },
    formatEventRange(event) {    
      const start = DateTime.fromJSDate(event.start).setLocale(this.$i18n.locale);
      
      const source = event.extendedProps ? event.extendedProps.source : event.source;

      // If FullCalendar dropped the end date because it matches the start date,
      // just return the start date formatted.
      if (!event.end) {
        if (source === 'leave') {
          return `${start.toFormat('dd.LL.yyyy')} - ${start.toFormat('dd.LL.yyyy')}`;
        }
        return `${start.toFormat('dd.LL.yyyy HH:mm')} - ${start.toFormat('dd.LL.yyyy HH:mm')}`;
      }
      
      let end = DateTime.fromJSDate(event.end).setLocale(this.$i18n.locale);

      if (source === 'leave') {
        // Only subtract a day if FullCalendar shifted the end date ahead
        if (end > start) {
          end = end.minus({ days: 1 });
        }
        return `${start.toFormat('dd.LL.yyyy')} - ${end.toFormat('dd.LL.yyyy')}`;
      }

      return `${start.toFormat('dd.LL.yyyy HH:mm')} - ${end.toFormat('dd.LL.yyyy HH:mm')}`;
    },
    eventType(event) {
      const source = event.extendedProps ? event.extendedProps.source : event.source;

      return source === 'reported'
        ? this.$t('messages.reported_service_time')
        : source === 'leave'
        ? this.$t('messages.leave_time')
        : this.$t('messages.scheduled_service_time');
    },
    setEventTitle(info) {
      const details = [
        this.eventType(info.event),
        this.formatEventRange(info.event),
        info.event.extendedProps.vip,
        info.event.extendedProps.location,
      ];
      info.el.title = details.filter(Boolean).join('\n');
    },
    openEvent(info) {
      this.selectedEvent = {
        source: info.event.extendedProps.source,
        reportId: info.event.extendedProps.reportId,
        start: info.event.start,
        end: info.event.end,
        vip: info.event.extendedProps.vip,
        location: info.event.extendedProps.location,
        amountOfDays: info.event.extendedProps.amountOfDays,
        car_type: info.event.extendedProps.car_type,
        license_plate: info.event.extendedProps.license_plate,
      };
      this.$refs.eventDetails.show();
    },
    openReport() {
      this.$refs.eventDetails.hide();
      this.$router.push({ name: 'reports.edit', params: { id: this.selectedEvent.reportId } });
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <header class="d-flex card-header text-white p-0 justify-content-between pr-0 bg-dark">
          <h5 class="p-2-5 mb-0">{{ $t('titles.calendar') }}</h5>
        </header>
      </b-col>
    </b-row>
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
      <div>
        <b-badge variant="info" class="mr-2 py-2 px-2">{{ $t('messages.scheduled_service_time') }}</b-badge>
        <b-badge variant="success" class="mr-2 py-2 px-2">{{ $t('messages.reported_service_time') }}</b-badge>
        <b-badge variant="warning" class="py-2 px-2 text-white">{{ $t('messages.leave_time') }}</b-badge>
      </div>
    </div>
    <div class="calendar-wrapper position-relative border bg-white p-2 p-md-3">
      <BlockingContainer v-if="loading" />
      <FullCalendar ref="calendar" :options="calendarOptions" />
    </div>
    <b-modal ref="eventDetails" :title="$t('titles.calendar_entry')" hide-header>
      <b-table-simple v-if="selectedEvent" borderless small>
        <b-tbody>
          <b-tr>
            <b-th>{{ $t('fields.type') }}</b-th>
            <b-td>{{ eventType(selectedEvent) }}</b-td>
          </b-tr>
          <b-tr>
            <b-th>{{ $t('fields.period') }}</b-th>
            <b-td>{{ formatEventRange(selectedEvent) }}</b-td>
          </b-tr>
          <template v-if="selectedEvent.source === 'leave'">
            <b-tr>
              <b-th>{{ $t('fields.amount_of_days') }}</b-th>
              <b-td>{{ selectedEvent.amountOfDays || '-' }}</b-td>
            </b-tr>
          </template>
          <template v-else>
            <b-tr>
              <b-th>{{ $t('fields.vip') }}</b-th>
              <b-td>{{ selectedEvent.vip || '-' }}</b-td>
            </b-tr>
            <b-tr>
              <b-th>{{ $t('fields.location') }}</b-th>
              <b-td>{{ selectedEvent.location || '-' }}</b-td>
            </b-tr>
            <b-tr>
              <b-th>{{ $t('fields.car_type') }}</b-th>
              <b-td>{{ selectedEvent.car_type || '-' }}</b-td>
            </b-tr>
            <b-tr v-if="selectedEvent.source === 'reported'">
              <b-th>{{ $t('table_labels.license_plate') }}</b-th>
              <b-td>{{ selectedEvent.license_plate || '-' }}</b-td>
            </b-tr>
          </template>
        </b-tbody>
      </b-table-simple>
      <template slot="modal-footer" slot-scope="{ close }">
        <div class="w-100 d-flex justify-content-end align-items-center">
          <div v-if="selectedEvent" class="d-flex">
            <b-button variant="secondary" @click="close()">{{ $t('actions.ok') }}</b-button>
            <b-button v-if="selectedEvent && selectedEvent.reportId" variant="dark" class="ml-2" @click="openReport">
              {{ $t('actions.open_report') }}
            </b-button>
          </div>
        </div>
      </template>      
    </b-modal>
  </Layout>
</template>
<style>
.calendar-wrapper {
  --fc-button-text-color: #ffffff;
  --fc-button-bg-color: #636363;
  --fc-button-border-color: #636363;
  --fc-button-hover-bg-color: #636363;
  --fc-button-hover-border-color: #636363;
  
  /* Active Button Variables (Native FullCalendar vars) */
  --fc-button-active-bg-color: #191919;
  --fc-button-active-border-color: #191919;

  /* Custom Variables for Links and Navigation (Our own vars) */
  --cal-link-color: #191919;
  --cal-link-hover-color: #636363;
  --cal-nav-btn-color: #191919;
  --cal-nav-btn-hover-color: #636363;
}

.calendar-wrapper .fc a.fc-col-header-cell-cushion,
.calendar-wrapper .fc a.fc-daygrid-day-number,
.calendar-wrapper .fc a.fc-timegrid-slot-label-cushion,
.calendar-wrapper .fc a.fc-timegrid-axis-cushion,
.calendar-wrapper .fc .fc-daygrid-more-link {
  color: var(--cal-link-color) !important;
  text-decoration: none !important;
}

.calendar-wrapper .fc a.fc-col-header-cell-cushion:hover,
.calendar-wrapper .fc a.fc-daygrid-day-number:hover,
.calendar-wrapper .fc .fc-daygrid-more-link:hover {
  color: var(--cal-link-hover-color) !important;
  text-decoration: underline !important;
}

.calendar-wrapper .fc .fc-button {
  border-radius: 0 !important;
  text-transform: capitalize !important;
  padding: 0.2em 0.45em !important;
  font-size: 14px !important;
}

.calendar-wrapper .fc .fc-prev-button,
.calendar-wrapper .fc .fc-next-button,
.calendar-wrapper .fc .fc-today-button {
  background-color: var(--cal-nav-btn-color) !important;
  border-color: var(--cal-nav-btn-color) !important;
}

.calendar-wrapper .fc .fc-prev-button:hover,
.calendar-wrapper .fc .fc-next-button:hover,
.calendar-wrapper .fc .fc-today-button:hover {
  background-color: var(--cal-nav-btn-hover-color) !important;
  border-color: var(--cal-nav-btn-hover-color) !important;
}

.calendar-wrapper .fc .fc-event {
  cursor: pointer !important;
}

.calendar-wrapper .fc-toolbar-title {
  font-size: clamp(1rem, 1vw + 0.5rem, 1.2rem) !important; 
}
</style>